import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { courtApi } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import GiftIcon from './icons/GiftIcon';
import { sportGlyph } from '../utils/sportGlyph';

// Reusable "Buy Credit" bottom sheet (F2). Context-aware: preselects
// `defaultCourtTypeId` but stays switchable. Select a pack, then confirm (bottom CTA).
// Sizing is deliberately large/legible (older customers).
const BuyCreditSheet = ({ open, onClose, defaultCourtTypeId, idToken, liff, userTokens }) => {
  const { t } = useLanguage();
  const [courtTypes, setCourtTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(defaultCourtTypeId || '');
  const [packs, setPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    courtApi.getCourtTypes().then(setCourtTypes).catch(() => {});
  }, [open]);

  useEffect(() => { if (open) setSelectedType(defaultCourtTypeId || ''); }, [open, defaultCourtTypeId]);

  useEffect(() => {
    setSelectedPack(null);
    if (!open || !selectedType) { setPacks([]); return; }
    setLoading(true); setError(null);
    courtApi.getCreditPacks(selectedType)
      .then(setPacks)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, selectedType]);

  const confirmPurchase = async () => {
    if (!selectedPack || !idToken) return;
    setSubmitting(true); setError(null);
    try {
      const res = await courtApi.createTopup(idToken, selectedPack.id, liff);
      if (res.paymentUrl) window.location.href = res.paymentUrl;
      else { setError(t('purchase_failed') || 'Purchase failed'); setSubmitting(false); }
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  if (!open) return null;
  const currentBalance = selectedType ? (userTokens?.[selectedType] || 0) : null;
  const selName = courtTypes.find((c) => c.id === selectedType)?.name || '';

  return createPortal((
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black bg-opacity-50" />
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[88vh] flex flex-col animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center pt-3 sm:hidden"><div className="w-12 h-1.5 bg-gray-300 rounded-full" /></div>

        {/* Scrollable content */}
        <div className="overflow-y-auto px-6 pt-5 pb-3">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2.5">
              <GiftIcon className="w-8 h-8 text-fuchsia-600" /> {t('promotion') || 'Promotion'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">✕</button>
          </div>

          <label className="block text-base font-medium text-gray-700 mb-2">{t('court_type') || 'Court type'}</label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="w-full px-4 py-3.5 text-lg border-2 border-gray-300 rounded-2xl mb-4 bg-white"
          >
            <option value="">{t('please_select') || '—'}</option>
            {courtTypes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          {/* Current balance for the selected court type */}
          {selectedType && (
            <div className="flex items-center justify-between mb-2 px-4 py-3 bg-amber-50 rounded-2xl">
              <span className="text-base text-gray-600">{t('your_tokens') || 'Hours remaining'}</span>
              <span className="flex items-center gap-2 text-xl font-bold text-amber-700">
                <span className="text-2xl leading-none">{sportGlyph(selName)}</span> {Number(currentBalance).toFixed(1)} {t('hours')}
              </span>
            </div>
          )}

          {/* Note: minimum booking is 30 minutes */}
          <div className="mb-5 text-sm">
            <p className="text-gray-500">📌 {t('booking_min_note')}</p>
            <p className="mt-1 text-gray-500">{t('credit_validity_note')}</p>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-base">{error}</div>}

          {!selectedType ? (
            <p className="text-center py-8 text-gray-400 text-base">{t('select_type_to_see_packs') || 'Select a court type to see packs'}</p>
          ) : loading ? (
            <p className="text-center py-8 text-gray-400 text-base">{t('loading') || 'Loading...'}</p>
          ) : packs.length === 0 ? (
            <p className="text-center py-8 text-gray-400 text-base">{t('no_packs_available') || 'No packs available'}</p>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {packs.map((p) => {
                const total = p.tokens + (p.bonusTokens || 0);
                const isSel = selectedPack?.id === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPack(p)}
                    className={`relative p-5 rounded-2xl border-2 text-center transition active:scale-95 ${
                      isSel ? 'border-indigo-500 ring-4 ring-indigo-100 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'
                    }`}
                  >
                    {p.bonusTokens > 0 && (
                      <span className="absolute -top-2.5 -right-2.5 bg-amber-400 text-white text-sm font-bold px-2.5 py-0.5 rounded-full">+{p.bonusTokens}🎁</span>
                    )}
                    {isSel && (
                      <span className="absolute top-2 left-2 w-6 h-6 bg-indigo-500 text-white rounded-full flex items-center justify-center text-sm">✓</span>
                    )}
                    <div className="text-4xl font-extrabold text-gray-900 leading-none">{total}</div>
                    <div className="text-sm text-gray-500 mt-1 mb-3">{t('hours') || 'hours'}</div>
                    <div className="text-indigo-600 font-bold text-xl">฿{p.price.toLocaleString()}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Sticky confirm CTA */}
        <div className="border-t border-gray-100 p-5 bg-white rounded-b-3xl sm:rounded-b-3xl">
          <button
            onClick={confirmPurchase}
            disabled={!selectedPack || submitting}
            className={`w-full py-4 rounded-2xl font-bold text-lg transition ${
              selectedPack && !submitting
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting
              ? (t('redirecting') || 'Redirecting...')
              : selectedPack
                ? `${t('pay_via_chillpay')} · ฿${selectedPack.price.toLocaleString()}`
                : (t('pay_via_chillpay') || 'Pay via QR')}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
};

export default BuyCreditSheet;
